import {
    projectMeasureParts,
    recordMeasureParts,
    type MeasureParts,
    type PhaseEntry,
    type ProjectDocType
} from '@ast-data/shared';

/**
 * The project header as its atomic parts — [Maßnahmennummer, Kurztitel,
 * Maßnahmenname] — derived from the current (latest) phase, blanks dropped.
 * Returned as parts so the app-bar breadcrumb renders each as its own level.
 */
export function projectTitleParts(project: ProjectDocType | null): string[] {
    if (!project) {
        return [];
    }
    const parts = projectMeasureParts({
        baseMeasureNumber: project.baseMeasureNumber,
        subMeasure: project.subMeasure,
        shortTitle: project.shortTitle,
        phases: (project.phases ?? []) as PhaseEntry[]
    });
    return [parts.measureNumber, parts.shortTitle, parts.measureName].filter(
        Boolean
    );
}

/** The composed project header as a single "a - b - c" string. */
export function projectDisplayTitle(project: ProjectDocType | null): string {
    return projectTitleParts(project).join(' - ');
}

/**
 * "{Maßnahmennummer} - {Maßnahmenname}" — the project named WITHOUT its
 * Kurztitel, blanks dropped. For chrome that has room for the measure name but
 * drops the short title (the office drawer's project submenu header).
 */
export function projectTitleWithoutShortTitle(
    project: ProjectDocType | null
): string {
    if (!project) {
        return '';
    }
    const parts = projectMeasureParts({
        baseMeasureNumber: project.baseMeasureNumber,
        subMeasure: project.subMeasure,
        shortTitle: project.shortTitle,
        phases: (project.phases ?? []) as PhaseEntry[]
    });
    return [parts.measureNumber, parts.measureName].filter(Boolean).join(' - ');
}

/**
 * The project's current Maßnahmennummer AND Kurztitel — as a PAIR, because they
 * belong to one another: a phase carries its own Kurztitel, so a phase-aware
 * number read next to a raw `project.shortTitle` captions one phase's number
 * with another phase's title. Anything that prints both takes them from here.
 */
export function projectMeasureInfo(project: ProjectDocType | null): {
    number: string;
    shortTitle: string;
} {
    if (!project) {
        return { number: '', shortTitle: '' };
    }
    const parts = projectMeasureParts({
        baseMeasureNumber: project.baseMeasureNumber,
        subMeasure: project.subMeasure,
        shortTitle: project.shortTitle,
        phases: (project.phases ?? []) as PhaseEntry[]
    });
    return { number: parts.measureNumber, shortTitle: parts.shortTitle };
}

/**
 * Just the project's current Maßnahmennummer (from its latest phase). The
 * record dialogs' app bar shows this alone — the Kurztitel is dropped there to
 * keep the breadcrumb short.
 */
export function projectMeasureNumber(project: ProjectDocType | null): string {
    return projectMeasureInfo(project).number;
}

/**
 * "{Maßnahmennummer} - {Kurztitel}" — the current measure number plus its short
 * title, blanks dropped. A compact label for project pickers and columns.
 */
export function projectMeasureLabel(project: ProjectDocType | null): string {
    const info = projectMeasureInfo(project);
    return [info.number, info.shortTitle].filter(Boolean).join(' - ');
}

/** What a record names its Maßnahme by: when it was taken, its own phase. */
export interface MeasureRecord {
    /** The record's day or capture time ('YYYY-MM-DD…'). */
    recordedAt: string;
    /** The record's own phase, where one is set ('' = from the day). */
    phaseNumber?: string;
}

/**
 * The parts a RECORD prints its Maßnahme under — the phase it was taken in,
 * not the project's current one (see recordMeasureParts). Every export that
 * names a record's Maßnahme takes them from here.
 */
export function recordMeasureInfo(
    project: ProjectDocType | null,
    record: MeasureRecord
): MeasureParts {
    if (!project) {
        return { measureNumber: '', shortTitle: '', measureName: '' };
    }
    const parts = recordMeasureParts(
        {
            baseMeasureNumber: project.baseMeasureNumber,
            subMeasure: project.subMeasure,
            shortTitle: project.shortTitle,
            phases: (project.phases ?? []) as PhaseEntry[]
        },
        record
    );
    // A Maßnahme not split into base, phase and Teilmaßnahme has only its
    // flat number to go by.
    return parts.measureNumber
        ? parts
        : { ...parts, measureNumber: project.measureNumber ?? '' };
}

/** "{Maßnahmennummer} - {Kurztitel}" of a record, blanks dropped. */
export function recordMeasureLabel(
    project: ProjectDocType | null,
    record: MeasureRecord
): string {
    const parts = recordMeasureInfo(project, record);
    return [parts.measureNumber, parts.shortTitle].filter(Boolean).join(' - ');
}

/**
 * The Maßnahmen a set of records falls under — one per phase they touch, in
 * the order of their days — for a sheet's head or a file name that covers
 * them all. Without records, the project's current one.
 */
export function recordsMeasureParts(
    project: ProjectDocType | null,
    records: readonly MeasureRecord[]
): MeasureParts[] {
    if (records.length === 0) {
        return [recordMeasureInfo(project, { recordedAt: '' })];
    }
    const byNumber = new Map<string, MeasureParts>();
    const sorted = [...records].sort((firstRecord, secondRecord) =>
        firstRecord.recordedAt.localeCompare(secondRecord.recordedAt)
    );
    for (const record of sorted) {
        const parts = recordMeasureInfo(project, record);
        if (!byNumber.has(parts.measureNumber)) {
            byNumber.set(parts.measureNumber, parts);
        }
    }
    return [...byNumber.values()];
}
